import { create } from "zustand";
import axios from "axios";
import { useFetcher } from "./fetcher";
import { Paging } from "../types/paging";
import {
  ProductEcomImportConfirmInput,
  ProductEcomImportErrorReportURL,
  ProductEcomImportInitUploadInput,
  ProductEcomImportInitUploadResponse,
  ProductEcomImportJob,
  ProductEcomImportListQuery,
} from "../types/product_ecom_import";

const SHOW_FILTER_KEY = "showFilter:productEcomImports";
const DEFAULT_LIMIT = 20;

// Direct S3 PUT cap (kept in sync with the backend's productEcomImportMaxBytes).
export const PRODUCT_ECOM_IMPORT_MAX_BYTES = 50 * 1024 * 1024;

// Snapshot of the currently-progressing upload so the drawer can render a
// progress bar and offer Cancel without re-issuing a new upload.
export interface ProductEcomImportUploadProgress {
  importId: number;
  filename: string;
  loadedBytes: number;
  totalBytes: number;
  // Only populated mid-upload — controller is cleared when done.
  abort?: AbortController;
}

interface ProductEcomImportsState {
  items: ProductEcomImportJob[];
  total: number | null;
  query: ProductEcomImportListQuery | null;
  showFilter: boolean;
  limit: number;
  // Live upload progress for the drawer; null when nothing's uploading.
  upload: ProductEcomImportUploadProgress | null;

  setShowFilter: (value: boolean) => void;
  search: (query?: ProductEcomImportListQuery, fetch?: boolean) => Promise<void>;
  fetch: (reset: boolean) => Promise<Paging<ProductEcomImportJob> | null>;
  list: (input?: ProductEcomImportListQuery) => Promise<Paging<ProductEcomImportJob>>;
  get: (id: number) => Promise<ProductEcomImportJob | null>;

  // uploadFile runs the full three-step flow: init-upload → PUT to S3 → confirm.
  // Throws on any step's failure so the drawer can surface inline errors.
  // `workspaceID` is forwarded as `workspace_id` to the backend on the
  // init-upload call; required when the SPA is on /ws/global.
  uploadFile: (file: File, workspaceID?: number | null) => Promise<ProductEcomImportJob>;
  cancelUpload: () => void;

  errorReportUrl: (id: number) => Promise<ProductEcomImportErrorReportURL>;
  // originalFileUrl returns a short-lived presigned URL for the originally
  // uploaded CSV (the file the operator submitted), reusing the same response
  // shape as errorReportUrl since both endpoints return {url, expires_at}.
  originalFileUrl: (id: number) => Promise<ProductEcomImportErrorReportURL>;
  // downloadTemplate fetches the CSV through the authenticated fetcher and
  // triggers a browser download. The endpoint is RBAC-gated, so opening it
  // in a new tab fails — only the fetcher attaches the Cognito JWT.
  downloadTemplate: () => Promise<void>;
  clear: () => void;
}

export const useProductEcomImportsStore = create<ProductEcomImportsState>(
  (set, get) =>
    ({
      items: [],
      total: null,
      query: null,
      showFilter: (() => {
        try {
          const v = localStorage.getItem(SHOW_FILTER_KEY);
          return v !== null ? JSON.parse(v) : false;
        } catch {
          return false;
        }
      })(),
      limit: DEFAULT_LIMIT,
      upload: null,

      setShowFilter: (value: boolean) => {
        set(() => ({ showFilter: value }));
        try {
          localStorage.setItem(SHOW_FILTER_KEY, JSON.stringify(value));
        } catch {}
      },

      search: async (query?: ProductEcomImportListQuery, fetch?: boolean) => {
        set(() => ({ query: query ?? null }));
        if (fetch !== false) {
          await get().fetch(true);
        }
      },

      fetch: async (
        reset: boolean
      ): Promise<Paging<ProductEcomImportJob> | null> => {
        const { items, limit, query, total } = get();
        if (!reset && total !== null && total <= items.length) {
          return null;
        }
        const params: Record<string, unknown> = {
          ...(query ?? {}),
          limit,
          offset: reset ? 0 : items.length,
        };
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get<Paging<ProductEcomImportJob>>(
            "product-ecoms/imports",
            { params, paramsSerializer: { indexes: null } }
          );
        set(() => ({
          items: [...(reset ? [] : items), ...(res?.data?.items ?? [])],
          total: res?.data?.total ?? null,
        }));
        return res?.data ?? null;
      },

      list: async (
        input?: ProductEcomImportListQuery
      ): Promise<Paging<ProductEcomImportJob>> => {
        const { limit, offset, ...query } = input
          ? input
          : { limit: DEFAULT_LIMIT, offset: 0 };
        const params: Record<string, unknown> = {
          ...query,
          limit: limit ?? DEFAULT_LIMIT,
          offset: offset ?? 0,
        };
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get<Paging<ProductEcomImportJob>>(
            "product-ecoms/imports",
            { params, paramsSerializer: { indexes: null } }
          );
        return res?.data ?? { total: 0, items: [] };
      },

      get: async (id: number): Promise<ProductEcomImportJob | null> => {
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get<ProductEcomImportJob>(`product-ecoms/imports/${id}`);
        return res?.data ?? null;
      },

      uploadFile: async (
        file: File,
        workspaceID?: number | null
      ): Promise<ProductEcomImportJob> => {
        if (file.size <= 0) {
          throw new Error("File is empty");
        }
        if (file.size > PRODUCT_ECOM_IMPORT_MAX_BYTES) {
          throw new Error(
            `File exceeds max size (${Math.round(
              PRODUCT_ECOM_IMPORT_MAX_BYTES / (1024 * 1024)
            )} MB)`
          );
        }

        // Step 1: ask backend for a presigned PUT. workspace_id is only sent
        // when the SPA is in /ws/global (backend ignores it from /ws/<id>).
        const initInput: ProductEcomImportInitUploadInput = {
          filename: file.name,
          size: file.size,
          ...(workspaceID != null ? { workspace_id: workspaceID } : {}),
        };
        const initRes = await useFetcher
          .getState()
          .omsDsFetcher.post<ProductEcomImportInitUploadResponse>(
            "product-ecoms/imports/upload-url",
            initInput
          );
        const init = initRes.data;

        // Step 2: PUT directly to S3. We use a bare axios instance (NOT the
        // OMS fetcher) because the presigned URL must not carry the Cognito
        // bearer header — that confuses S3's signature check.
        const abort = new AbortController();
        set(() => ({
          upload: {
            importId: init.import_id,
            filename: file.name,
            loadedBytes: 0,
            totalBytes: file.size,
            abort,
          },
        }));
        try {
          await axios.put(init.upload_url, file, {
            headers: init.headers ?? { "Content-Type": "text/csv" },
            signal: abort.signal,
            onUploadProgress: (e) => {
              const cur = get().upload;
              if (!cur || cur.importId !== init.import_id) return;
              set(() => ({
                upload: {
                  ...cur,
                  loadedBytes: e.loaded,
                  totalBytes: e.total ?? cur.totalBytes,
                },
              }));
            },
          });
        } catch (err) {
          set(() => ({ upload: null }));
          throw err;
        }

        // Step 3: confirm so the backend HEADs the object and enqueues SQS.
        const confirmInput: ProductEcomImportConfirmInput = {
          import_id: init.import_id,
        };
        const confirmRes = await useFetcher
          .getState()
          .omsDsFetcher.post<ProductEcomImportJob>(
            "product-ecoms/imports",
            confirmInput
          );
        set(() => ({ upload: null }));
        return confirmRes.data;
      },

      cancelUpload: () => {
        const cur = get().upload;
        if (cur?.abort) cur.abort.abort();
        set(() => ({ upload: null }));
      },

      errorReportUrl: async (
        id: number
      ): Promise<ProductEcomImportErrorReportURL> => {
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get<ProductEcomImportErrorReportURL>(
            `product-ecoms/imports/${id}/errors`
          );
        return res.data;
      },

      originalFileUrl: async (
        id: number
      ): Promise<ProductEcomImportErrorReportURL> => {
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get<ProductEcomImportErrorReportURL>(
            `product-ecoms/imports/${id}/original`
          );
        return res.data;
      },

      downloadTemplate: async () => {
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get("product-ecoms/imports/template", {
            headers: { Accept: "text/csv" },
            responseType: "blob",
          });
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = "product_ecom_import_template.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      },

      clear: () => {
        set(() => ({ items: [], total: null, query: null, upload: null }));
      },
    } as ProductEcomImportsState)
);
